import {
  CalendarIcon,
  ChartBarIcon,
  SquaresPlusIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { TuneRounded } from '@mui/icons-material';
import SettingsIcon from '@mui/icons-material/Settings';
import {
  Box,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo, useEffect, useState } from 'react';
import { Project, ReportsProjectFilter } from 'src/@types/project';
import { Teammate } from 'src/@types/teammates';
import { CustomAvatar } from 'src/components/custom-avatar';
import MenuPopover from 'src/components/menu-popover';
import { OutreachTypeAvatar } from 'src/components/outreach-type-avatar';
import { useProjects } from 'src/hooks/query/projectHooks';
import { useTeammates } from 'src/hooks/query/userHooks';
import { useTeams } from 'src/hooks/query/teamHooks';
import { formatUserName } from 'src/utils/formatUserName';
import { getRegisteredTeammates } from 'src/utils/getRegisteredTeammates';
import { ReportsGeographyFilter, ReportsGeographyType } from 'src/@types/geographyFilters';
import { useReportsGeographyFilters } from 'src/hooks/query/geographyFilterHooks';
import { ReportsTeamFilter } from 'src/@types/team';
import { outreachTypes } from 'src/assets/data/outreachTypes';
import { OutreachTypeValues, TOutreachType, formatOutreachType } from 'src/@types/outreachType';
import ReportsFiltersChips from './ReportsFiltersChips';
import escapeRegExp from 'lodash/escapeRegExp';
import DateRangePicker, { Range } from 'src/components/date-range-picker';
import { formatGeofenceName, sortGeofences } from 'src/utils/geofenceUtils';
import { startOfDay, endOfDay } from 'date-fns';

type ReportsFiltersProps = {
  setFilteredUser?: (user: Teammate | Partial<Teammate> | null) => void;
  filteredUser?: Teammate | Partial<Teammate> | null;
  setFilteredProject?: (project: ReportsProjectFilter | null) => void;
  filteredProject?: ReportsProjectFilter | null;
  setFilteredDateRange?: (daterange: Range | null) => void;
  filteredDateRange?: Range | null;
  setFilteredCounty?: (county: ReportsGeographyFilter | null) => void;
  filteredCounty?: ReportsGeographyFilter | null;
  setFilteredPrecinct?: (precinct: ReportsGeographyFilter | null) => void;
  filteredPrecinct?: ReportsGeographyFilter | null;
  setFilteredCongressionalDistrict?: (congressionalDistrict: ReportsGeographyFilter | null) => void;
  filteredCongressionalDistrict?: ReportsGeographyFilter | null;
  setFilteredStateHouseDistrict?: (stateHouseDistrict: ReportsGeographyFilter | null) => void;
  filteredStateHouseDistrict?: ReportsGeographyFilter | null;
  setFilteredStateSenateDistrict?: (stateSenateDistrict: ReportsGeographyFilter | null) => void;
  filteredStateSenateDistrict?: ReportsGeographyFilter | null;
  setFilteredDma?: (dma: ReportsGeographyFilter | null) => void;
  filteredDma?: ReportsGeographyFilter | null;
  setFilteredTeam?: (team: ReportsTeamFilter | null) => void;
  filteredTeam?: ReportsTeamFilter | null;
  setFilteredOutreachType?: (outreachType: TOutreachType | null) => void | null;
  filteredOutreachType?: TOutreachType | null;
  showChips?: boolean;
  shouldGroupCanvassesByHousehold?: boolean;
  setShouldGroupCanvassesByHousehold?: (_: boolean) => void;
  extraFilters?: React.ReactNode;
  tooltipText?: string;
};

export default function ReportsFilters({
  setFilteredUser,
  filteredUser,
  setFilteredProject,
  filteredProject,
  setFilteredDateRange,
  filteredDateRange,
  setFilteredCounty,
  filteredCounty,
  setFilteredPrecinct,
  filteredPrecinct,
  setFilteredCongressionalDistrict,
  filteredCongressionalDistrict,
  setFilteredStateHouseDistrict,
  filteredStateHouseDistrict,
  setFilteredStateSenateDistrict,
  filteredStateSenateDistrict,
  setFilteredDma,
  filteredDma,
  setFilteredTeam,
  filteredTeam,
  setFilteredOutreachType,
  filteredOutreachType,
  shouldGroupCanvassesByHousehold,
  setShouldGroupCanvassesByHousehold,
  showChips = true,
  extraFilters,
  tooltipText,
}: ReportsFiltersProps) {
  const theme = useTheme();
  const { data: projects } = useProjects({
    enabled: !!setFilteredProject,
  });

  const formattedProjects = useMemo(() => {
    if (!projects) return [];
    const sortedProjects = projects.sort((a, b) => {
      if (a.name.localeCompare(b.name) < 0) return -1;
      if (a.name.localeCompare(b.name) > 0) return 1;
      return sortGeofences(a.geofence_name || '', b.geofence_name || '');
    });

    return sortedProjects.map((project) => {
      const projectLabel = [project.county_name, project.precinct_id, project.precinct_name]
        .filter((label) => !!label)
        .join('-');

      return {
        ...project,
        name: project.geofence_name
          ? `${project.name} (${formatGeofenceName(project.geofence_name)})`
          : projectLabel
            ? `${project.name} (${projectLabel})`
            : project.name,
      };
    });
  }, [projects]);

  const getSetGeographyFilter = (geographyType: ReportsGeographyType) => {
    switch (geographyType) {
      case 'county_name':
        return setFilteredCounty;
      case 'precinct_name':
        return setFilteredPrecinct;
      case 'congressional_district':
        return setFilteredCongressionalDistrict;
      case 'state_leg_upper_district':
        return setFilteredStateSenateDistrict;
      case 'state_leg_lower_district':
        return setFilteredStateHouseDistrict;
      case 'dma_name':
        return setFilteredDma;
    }
  };

  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);
  const [filterType, setFilterType] = useState<
    'user' | 'team' | 'user_team' | 'project' | 'date' | 'outreach' | ReportsGeographyType | null
  >(null);
  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const [openSettingsPopover, setOpenSettingsPopover] = useState<HTMLElement | null>(null);
  const handleOpenSettingsPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenSettingsPopover(event.currentTarget);
  };

  const [alignment, setAlignment] = useState('users');
  const handleAlignmentChange = (event: React.MouseEvent<HTMLElement>, newAlignment: string) => {
    setAlignment(newAlignment);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
    setOpenSettingsPopover(null);
    setFilterType(null);
  };

  useEffect(() => {
    handleClosePopover();
  }, [
    filteredUser,
    filteredDateRange,
    filteredTeam,
    filteredProject,
    filteredOutreachType,
    filteredCounty,
    filteredPrecinct,
    filteredCongressionalDistrict,
    filteredStateHouseDistrict,
    filteredStateSenateDistrict,
    filteredDma,
  ]);

  const getFilterContent = () => {
    if (setFilteredUser && setFilteredTeam && filterType === 'user_team') {
      return (
        <UserTeamToggleFilter
          alignment={alignment}
          handleChange={handleAlignmentChange}
          onUserFilterSelect={setFilteredUser}
          onTeamFilterSelect={setFilteredTeam}
        />
      );
    } else if (filterType === 'user' && setFilteredUser) {
      return <UserFilter onFilterSelect={setFilteredUser} />;
    } else if (filterType === 'project' && setFilteredProject) {
      return <ProjectFilter onFilterSelect={setFilteredProject} projects={formattedProjects} />;
    } else if (filterType === 'team' && setFilteredTeam) {
      return <TeamFilter onFilterSelect={setFilteredTeam} />;
    } else if (filterType === 'date' && setFilteredDateRange) {
      return (
        <DateRangeFilter
          onFilterSelect={setFilteredDateRange}
          currentRange={filteredDateRange || undefined}
        />
      );
    } else if (
      filterType === 'county_name' ||
      filterType === 'precinct_name' ||
      filterType === 'congressional_district' ||
      filterType === 'state_leg_upper_district' ||
      filterType === 'state_leg_lower_district' ||
      filterType === 'dma_name'
    ) {
      const foundGeographyFilter = getSetGeographyFilter(filterType);

      if (!foundGeographyFilter) return null;
      return <GeographyFilter onFilterSelect={foundGeographyFilter} geographyType={filterType} />;
    } else if (filterType === 'outreach' && setFilteredOutreachType) {
      return <OutreachFilter onFilterSelect={setFilteredOutreachType} />;
    }

    return (
      <List>
        {setFilteredOutreachType && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => setFilterType('outreach')}>
              <ListItemIcon>
                <ChartBarIcon width={18} color={theme.palette.secondary.main} />
              </ListItemIcon>
              <ListItemText primary="Outreach Type" />
            </ListItemButton>
          </ListItem>
        )}
        {setFilteredUser && setFilteredTeam && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => setFilterType('user_team')}>
              <ListItemIcon>
                <UserCircleIcon width={18} color={theme.palette.secondary.main} />
              </ListItemIcon>
              <ListItemText primary="User/Team" />
            </ListItemButton>
          </ListItem>
        )}
        {setFilteredProject && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => setFilterType('project')}>
              <ListItemIcon>
                <SquaresPlusIcon width={18} color={theme.palette.secondary.main} />
              </ListItemIcon>
              <ListItemText primary="Project" />
            </ListItemButton>
          </ListItem>
        )}
        {setFilteredDateRange && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => setFilterType('date')}>
              <ListItemIcon>
                <CalendarIcon width={18} color={theme.palette.secondary.main} />
              </ListItemIcon>
              <ListItemText primary="Date Range" />
            </ListItemButton>
          </ListItem>
        )}
      </List>
    );
  };

  const tooltipOptions = [];
  if (setFilteredUser && setFilteredTeam) {
    tooltipOptions.push('User/Team');
  }
  if (setFilteredProject) {
    tooltipOptions.push('Project');
  }
  if (setFilteredDateRange) {
    tooltipOptions.push('Date');
  }
  if (tooltipOptions.length > 1) {
    tooltipOptions.push('etc.');
  }
  const tooltipTextInternal =
    tooltipOptions.length > 0 ? `Filter by ${tooltipOptions.join(', ')}` : '';

  return (
    <Stack direction="row" alignItems="center">
      {showChips && (
        <ReportsFiltersChips
          filteredUser={filteredUser}
          filteredProject={filteredProject}
          filteredDateRange={filteredDateRange}
          filteredCounty={filteredCounty}
          filteredPrecinct={filteredPrecinct}
          filteredCongressionalDistrict={filteredCongressionalDistrict}
          filteredStateHouseDistrict={filteredStateHouseDistrict}
          filteredStateSenateDistrict={filteredStateSenateDistrict}
          filteredDma={filteredDma}
          filteredTeam={filteredTeam}
          filteredOutreachType={filteredOutreachType}
          setFilteredUser={setFilteredUser}
          setFilteredProject={setFilteredProject}
          setFilteredDateRange={setFilteredDateRange}
          setFilteredCounty={setFilteredCounty}
          setFilteredPrecinct={setFilteredPrecinct}
          setFilteredCongressionalDistrict={setFilteredCongressionalDistrict}
          setFilteredStateHouseDistrict={setFilteredStateHouseDistrict}
          setFilteredStateSenateDistrict={setFilteredStateSenateDistrict}
          setFilteredDma={setFilteredDma}
          setFilteredTeam={setFilteredTeam}
          setFilteredOutreachType={setFilteredOutreachType}
        />
      )}
      <Tooltip title={tooltipText || tooltipTextInternal} arrow>
        <IconButton onClick={handleOpenPopover}>
          <TuneRounded />
        </IconButton>
      </Tooltip>
      <MenuPopover open={openPopover} onClose={handleClosePopover}>
        {getFilterContent()}
      </MenuPopover>
      {setShouldGroupCanvassesByHousehold && (
        <>
          <Tooltip title="Reports Settings" arrow>
            <IconButton onClick={handleOpenSettingsPopover}>
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <MenuPopover
            open={openSettingsPopover}
            onClose={handleClosePopover}
            sx={{ px: 2.5, py: 2 }}
          >
            <Stack gap={1} sx={{ width: 250 }}>
              <Stack>
                <Typography variant="subtitle1">Show canvass count as:</Typography>
                <Stack direction="row" alignItems="center">
                  <Typography fontWeight={!shouldGroupCanvassesByHousehold ? 'bold' : undefined}>
                    Voters
                  </Typography>
                  <Switch
                    checked={shouldGroupCanvassesByHousehold}
                    onChange={(event) => setShouldGroupCanvassesByHousehold(event.target.checked)}
                  />
                  <Typography fontWeight={shouldGroupCanvassesByHousehold ? 'bold' : undefined}>
                    Households
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Households are computed for canvassing based on unique doors contacted per day.
                  Contacting the same door multiple times in one day will count as one household.
                </Typography>
              </Stack>
              {extraFilters}
            </Stack>
          </MenuPopover>
        </>
      )}
    </Stack>
  );
}

type UserTeamFilterProps = {
  alignment: string;
  handleChange: (event: React.MouseEvent<HTMLElement>, newAlignment: string) => void;
  onUserFilterSelect: (filterValue: Teammate) => void;
  onTeamFilterSelect: (filterValue: ReportsTeamFilter) => void;
};

function UserTeamToggleFilter({
  alignment,
  handleChange,
  onUserFilterSelect,
  onTeamFilterSelect,
}: UserTeamFilterProps) {
  return (
    <Stack>
      <ToggleButtonGroup
        color="primary"
        value={alignment}
        exclusive
        onChange={handleChange}
        aria-label="Assignee Type"
        sx={{ width: '100%', mb: 1 }}
      >
        <ToggleButton sx={{ width: '50%', height: 35 }} value="users">
          Users
        </ToggleButton>
        <ToggleButton sx={{ width: '50%', height: 35 }} value="teams">
          Teams
        </ToggleButton>
      </ToggleButtonGroup>
      {alignment === 'users' ? (
        <UserFilter onFilterSelect={onUserFilterSelect} />
      ) : (
        <TeamFilter onFilterSelect={onTeamFilterSelect} />
      )}
    </Stack>
  );
}

type UserFilterProps = {
  onFilterSelect: (filterValue: Teammate) => void;
};

function UserFilter({ onFilterSelect }: UserFilterProps) {
  const { data: teammates, isPending } = useTeammates();
  const registeredTeammates = getRegisteredTeammates(teammates || []);

  const [searchText, setSearchText] = useState('');

  let searchedTeammates = registeredTeammates;

  if (searchText) {
    const escapedSearchText = escapeRegExp(searchText);
    const searchRegExp = new RegExp(escapedSearchText, 'i');
    searchedTeammates = registeredTeammates?.filter((teammate) =>
      searchRegExp.test(`${teammate.first_name} ${teammate.last_name}`)
    );
  }

  return (
    <List sx={{ minWidth: 300, overflow: 'auto', maxHeight: 500 }}>
      {isPending && (
        <Stack alignItems="center">
          <CircularProgress size={18} />
        </Stack>
      )}
      {!isPending && (
        <TextField
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          fullWidth
          size="small"
          placeholder="Search users ..."
          sx={{ mb: 1 }}
        />
      )}
      {searchedTeammates?.map((teammate) => (
        <ListItem disablePadding key={teammate.id}>
          <ListItemButton onClick={() => onFilterSelect(teammate)}>
            <ListItemIcon>
              <CustomAvatar
                sx={{ width: 20, height: 20, fontSize: 12 }}
                name={formatUserName(teammate.first_name, teammate.last_name)}
              />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography variant="body2">
                  {formatUserName(teammate.first_name, teammate.last_name)}
                </Typography>
              }
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  );
}

type TeamFilterProps = {
  onFilterSelect: (filterValue: ReportsTeamFilter) => void;
};

function TeamFilter({ onFilterSelect }: TeamFilterProps) {
  const { data: teams, isPending } = useTeams();

  const [searchText, setSearchText] = useState('');

  let searchedTeams = teams;

  if (searchText) {
    const escapedSearchText = escapeRegExp(searchText);
    const searchRegExp = new RegExp(escapedSearchText, 'i');
    searchedTeams = teams?.filter((team) => searchRegExp.test(team.name));
  }

  return (
    <Box maxHeight="80vh" overflow="auto">
      <List sx={{ minWidth: 300 }}>
        {isPending && (
          <Stack alignItems="center">
            <CircularProgress size={18} />
          </Stack>
        )}
        {!isPending && (
          <TextField
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            fullWidth
            size="small"
            placeholder="Search teams ..."
            sx={{ mb: 1 }}
          />
        )}
        {searchedTeams?.map((team) => (
          <ListItem disablePadding key={team.id}>
            <ListItemButton onClick={() => onFilterSelect(team)}>
              <ListItemIcon>
                <CustomAvatar sx={{ width: 20, height: 20, fontSize: 12 }} name={team.name} />
              </ListItemIcon>
              <ListItemText primary={<Typography variant="body2">{team.name}</Typography>} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

type ProjectFilterProps = {
  onFilterSelect: (filterValue: ReportsProjectFilter) => void;
  projects: Project[];
};

function ProjectFilter({ onFilterSelect, projects }: ProjectFilterProps) {
  const [searchText, setSearchText] = useState('');
  const [outreachType, setOutreachType] = useState<string>('all');

  const outreachOptions = ['all', ...outreachTypes];

  let searchedProjects = projects;

  if (outreachType !== 'all') {
    searchedProjects = projects.filter((project) => project.outreach_type === outreachType);
  }

  if (searchText) {
    searchedProjects = searchedProjects.filter((project) =>
      new RegExp(escapeRegExp(searchText), 'i').test(project.name)
    );
  }

  return (
    <Box maxHeight="80vh" overflow="auto">
      <List sx={{ minWidth: 300 }}>
        <TextField
          fullWidth
          select
          label="Outreach Type"
          size="small"
          value={outreachType}
          onChange={(event) => setOutreachType(event.target.value)}
          slotProps={{
            select: {
              MenuProps: {
                PaperProps: {
                  sx: { maxHeight: 220 },
                },
              },
            },
          }}
          sx={{
            textTransform: 'capitalize',
            mb: 1,
          }}
        >
          {outreachOptions.map((option) => (
            <MenuItem key={option} value={option}>
              {option === 'ivr' ? option.toUpperCase() : option}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          fullWidth
          size="small"
          placeholder="Search projects ..."
          sx={{ mb: 1 }}
        />
        {searchedProjects.map((project) => (
          <ListItem disablePadding key={project.id}>
            <ListItemButton onClick={() => onFilterSelect(project)}>
              <ListItemIcon>
                <OutreachTypeAvatar outreachType={project.outreach_type} size={28} />
              </ListItemIcon>
              <ListItemText primary={<Typography variant="body2">{project.name}</Typography>} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

type DateRangeFilterProps = {
  onFilterSelect: (filterValue: Range) => void;
  currentRange?: Range;
};

function DateRangeFilter({ onFilterSelect, currentRange }: DateRangeFilterProps) {
  const [dateRange, setDateRange] = useState<Range>({
    startDate: startOfDay(currentRange?.startDate || new Date()),
    endDate: endOfDay(currentRange?.endDate || new Date()),
    key: 'selection',
  });
  const [customRangeOpen, setCustomRangeOpen] = useState(false);

  const today = new Date();
  const todayStart = startOfDay(today);
  const todayEnd = endOfDay(today);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yesterdayStart = startOfDay(yesterday);
  const yesterdayEnd = endOfDay(yesterday);

  const lastWeek = new Date();
  lastWeek.setDate(lastWeek.getDate() - 7);
  const lastWeekStart = startOfDay(lastWeek);

  const lastMonth = new Date();
  const thisMonth = lastMonth.getMonth();
  lastMonth.setMonth(thisMonth - 1);
  // since not all months have 31 days we need to account for that
  // by setting the date to the last day of the previous month
  if (lastMonth.getMonth() === thisMonth) {
    lastMonth.setDate(0);
  }
  const lastMonthStart = startOfDay(lastMonth);

  useEffect(() => {
    if (currentRange) {
      setDateRange({
        key: 'selection',
        startDate: startOfDay(currentRange.startDate || new Date()),
        endDate: endOfDay(currentRange.endDate || new Date()),
      });
    }
  }, [currentRange]);

  return (
    <>
      <DateRangePicker
        open={customRangeOpen}
        onClose={() => setCustomRangeOpen(false)}
        startDate={dateRange.startDate || null}
        endDate={dateRange.endDate || null}
        onChangeDateRange={(range) => {
          const start = range[0] ? startOfDay(range[0]) : undefined;
          const end = range[1] ? endOfDay(range[1]) : undefined;

          setDateRange({
            key: 'selection',
            startDate: start,
            endDate: end,
          });
          onFilterSelect({
            key: 'selection',
            startDate: start,
            endDate: end,
          });
        }}
      />
      <List>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() =>
              onFilterSelect({ key: 'Today', startDate: todayStart, endDate: todayEnd })
            }
          >
            <ListItemText primary="Today" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() =>
              onFilterSelect({ key: 'Yesterday', startDate: yesterdayStart, endDate: yesterdayEnd })
            }
          >
            <ListItemText primary="Yesterday" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() =>
              onFilterSelect({ key: 'Past Week', startDate: lastWeekStart, endDate: todayEnd })
            }
          >
            <ListItemText primary="Past Week" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            onClick={() =>
              onFilterSelect({ key: 'Past Month', startDate: lastMonthStart, endDate: todayEnd })
            }
          >
            <ListItemText primary="Past Month" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton onClick={() => setCustomRangeOpen(true)}>
            <ListItemText primary="Custom Range" />
          </ListItemButton>
        </ListItem>
      </List>
    </>
  );
}

type GeographyFilterProps = {
  onFilterSelect: (filterValue: ReportsGeographyFilter) => void;
  geographyType: ReportsGeographyType;
};

function GeographyFilter({ onFilterSelect, geographyType }: GeographyFilterProps) {
  const { data: geographies, isPending } = useReportsGeographyFilters({
    geography_type: geographyType,
  });

  return (
    <Box maxHeight="80vh" overflow="auto">
      <List sx={{ minWidth: 300 }}>
        {isPending && (
          <Stack alignItems="center">
            <CircularProgress size={18} />
          </Stack>
        )}
        {geographies?.map((geography) => (
          <ListItem disablePadding key={geography.id}>
            <ListItemButton onClick={() => onFilterSelect(geography)}>
              <ListItemText primary={<Typography variant="body2">{geography.name}</Typography>} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}

type OutreachFilterProps = {
  onFilterSelect: (filterValue: TOutreachType) => void;
};

function OutreachFilter({ onFilterSelect }: OutreachFilterProps) {
  const outreachTypes = OutreachTypeValues;

  return (
    <Box maxHeight="80vh" overflow="auto">
      <List sx={{ minWidth: 300 }}>
        {outreachTypes?.map((outreachType) => (
          <ListItem disablePadding key={outreachType}>
            <ListItemButton onClick={() => onFilterSelect(outreachType)}>
              <ListItemText
                primary={
                  <Typography variant="body2">{formatOutreachType(outreachType)}</Typography>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
